import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import Sidebar from './components/Sidebar';
import LoginPage from './components/LoginPage';
import api, { setUnauthorizedHandler } from './services/api';

import MoradoresPage from './pages/Moradores/MoradoresPage';
import ContratadosPage from './pages/Contratados/ContratadosPage';
import VisitantesPage from './pages/Visitantes/VisitantesPage';
import AirbnbPage from './pages/Visitantes/AirbnbPage';
import AgendaPage from './pages/Agenda/AgendaPage';
import EntregaPage from './pages/Entregas/EntregaPage';
import DeliveryPage from './pages/Delivery/DeliveryPage';
import DispositivoPage from './pages/Dispositivos/DispositivoPage';
import GrupoPage from './pages/Dispositivos/GrupoPage';
import AberturaPage from './pages/Abertura/AberturaPage';
import CadastroMassaPage from './pages/Abertura/CadastroMassaPage';
import ImportarPage from './pages/Backup/BackupPage';
import SystemBackupPage from './pages/Backup/SystemBackupPage';
import MonitorPage from './pages/Monitor/MonitorPage';
import ControleAcessoPage from './pages/Monitor/ControleAcessoPage';
import WhatsAppPage from './pages/WhatsApp/WhatsAppPage';
import ConfiguracoesPage from './pages/Configuracoes/ConfiguracoesPage';
import UsuariosSistemaPage from './pages/UsuariosSistema/UsuariosSistemaPage';
import PainelPage from './pages/Painel/PainelPage';
import LogsPage from './pages/Logs/LogsPage';
import ChangeInitialPassword from './components/ChangeInitialPassword';
import { LicensePlanProvider, PlanGate } from './components/LicensePlan';
import { getFirstAllowedRoute, hasPermission } from './utils/permissions';
// developer-only:start
import DeveloperPage from './pages/Developer/DeveloperPage';
// developer-only:end

function AppContent() {
  const [auth, setAuth] = useState(() => {
    const username = localStorage.getItem('authUser');
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('authUserData');
    const permissionsData = localStorage.getItem('authPermissions');
    if (!username || !token) return null;
    try {
      return {
        user: userData ? JSON.parse(userData) : { login: username },
        permissions: permissionsData ? JSON.parse(permissionsData) : {},
      };
    } catch (error) {
      return { user: { login: username }, permissions: {} };
    }
  });
  const authenticatedUser = auth?.user?.login || null;
  const [theme, setTheme] = useState(() => localStorage.getItem('appTheme') || 'light');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarHidden, setIsDesktopSidebarHidden] = useState(() => localStorage.getItem('desktopSidebarHidden') === 'true');


  const handleLogout = useCallback(() => {
    const token = localStorage.getItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUserData');
    localStorage.removeItem('authPermissions');
    setAuth(null);
    setIsSidebarOpen(false);
    if (token) api.post('/auth/logout', null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      handleLogout();
    });

    return () => setUnauthorizedHandler(null);
  }, [handleLogout]);

  useEffect(() => {
    const root = document.documentElement;
    const activeTheme = authenticatedUser ? theme : 'light';
    root.classList.toggle('theme-dark', activeTheme === 'dark');
    root.classList.toggle('theme-light', activeTheme !== 'dark');
    localStorage.setItem('appTheme', theme);
  }, [authenticatedUser, theme]);

  useEffect(() => {
    localStorage.setItem('desktopSidebarHidden', String(isDesktopSidebarHidden));
  }, [isDesktopSidebarHidden]);

  useEffect(() => {
    if (!authenticatedUser) return;
    let active = true;
    const refreshIdentity = async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (!active) return;
        localStorage.setItem('authUserData', JSON.stringify(data));
        localStorage.setItem('authPermissions', JSON.stringify(data.permissoes));
        setAuth({ user: data, permissions: data.permissoes });
      } catch (_) {}
    };
    refreshIdentity();
    const timer = setInterval(refreshIdentity, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [authenticatedUser]);

  if (!authenticatedUser) {
    return <LoginPage onLoginSuccess={setAuth} />;
  }

  if (auth.user?.trocar_senha_primeiro_acesso) {
    return <ChangeInitialPassword onLogout={handleLogout} onChanged={(user) => { localStorage.setItem('authUserData', JSON.stringify(user)); localStorage.setItem('authPermissions', JSON.stringify(user.permissoes)); setAuth({ user, permissions: user.permissoes }); }} />;
  }

  return (
    <div className="app-shell h-[100dvh] overflow-hidden bg-condo-bg transition-colors duration-200">
      <button
        type="button"
        onClick={() => setIsSidebarOpen((current) => !current)}
        aria-label={isSidebarOpen ? 'Fechar menu' : 'Abrir menu'}
        title={isSidebarOpen ? 'Fechar menu' : 'Abrir menu'}
        className={`fixed top-4 z-50 flex h-11 w-11 items-center justify-center rounded-xl bg-condo-dark text-white shadow-lg transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-condo-teal lg:hidden ${
          isSidebarOpen ? 'right-4' : 'left-4'
        }`}
      >
        {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <button
        type="button"
        onClick={() => setIsDesktopSidebarHidden((current) => !current)}
        aria-label={isDesktopSidebarHidden ? 'Mostrar menu lateral' : 'Ocultar menu lateral'}
        title={isDesktopSidebarHidden ? 'Mostrar menu lateral' : 'Ocultar menu lateral'}
        className={`fixed top-4 z-50 hidden h-11 w-11 items-center justify-center rounded-xl bg-condo-dark text-white shadow-lg transition-all hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-condo-teal lg:flex ${
          isDesktopSidebarHidden ? 'left-4' : 'left-[12.75rem]'
        }`}
      >
        {isDesktopSidebarHidden ? <Menu size={22} /> : <X size={22} />}
      </button>

      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-30 bg-slate-950/45 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <Sidebar
        isOpen={isSidebarOpen}
        onNavigate={() => setIsSidebarOpen(false)}
        onLogout={handleLogout}
        auth={auth}
        isDesktopHidden={isDesktopSidebarHidden}
        theme={theme}
        onThemeChange={setTheme}
      />

      <main className={`app-content h-[100dvh] overflow-y-auto overflow-x-hidden bg-condo-bg px-4 pt-20 pb-[calc(1.5rem+env(safe-area-inset-bottom))] transition-[padding] duration-200 sm:px-6 lg:pr-8 lg:pt-8 lg:pb-8 ${
        isDesktopSidebarHidden ? 'lg:pl-8' : 'lg:pl-64'
      }`}>
        <Routes>
          <Route path="/" element={<Navigate to={getFirstAllowedRoute(auth)} replace />} />
          <Route path="/inicio" element={<ProtectedRoute auth={auth} modulo="inicio"><PainelPage auth={auth} /></ProtectedRoute>} />
          <Route path="/logs" element={<ProtectedRoute auth={auth} modulo="logs"><LogsPage /></ProtectedRoute>} />
          <Route path="/operacao" element={<Navigate to="/inicio" replace />} />
          <Route
            path="/morador"
            element={
              <ProtectedRoute auth={auth} modulo="morador">
              <MoradoresPage
                authenticatedUser={authenticatedUser}
                onLogout={handleLogout}
              />
              </ProtectedRoute>
            }
          />
          <Route path="/agenda" element={<ProtectedRoute auth={auth} modulo="agenda"><AgendaPage /></ProtectedRoute>} />
          <Route path="/visitante" element={<ProtectedRoute auth={auth} modulo="visitante"><VisitantesPage /></ProtectedRoute>} />
          <Route path="/airbnb" element={<ProtectedRoute auth={auth} modulo="airbnb"><AirbnbPage auth={auth} /></ProtectedRoute>} />
          <Route path="/contratado" element={<ProtectedRoute auth={auth} modulo="contratado"><ContratadosPage /></ProtectedRoute>} />
          <Route path="/entrega" element={<ProtectedRoute auth={auth} modulo="entrega"><EntregaPage /></ProtectedRoute>} />
          <Route path="/delivery" element={<ProtectedRoute auth={auth} modulo="delivery"><DeliveryPage /></ProtectedRoute>} />
          <Route path="/dispositivos" element={<ProtectedRoute auth={auth} modulo="dispositivos"><DispositivoPage /></ProtectedRoute>} />
          <Route path="/grupos" element={<ProtectedRoute auth={auth} modulo="grupos"><GrupoPage auth={auth} /></ProtectedRoute>} />
          <Route path="/controle-acesso" element={<ProtectedRoute auth={auth} modulo="controle_acesso"><MonitorPage auth={auth} /></ProtectedRoute>} />
          <Route path="/controle-acesso/cameras" element={<ProtectedRoute auth={auth} modulo="cameras"><ControleAcessoPage /></ProtectedRoute>} />
          <Route path="/abertura" element={<ProtectedRoute auth={auth} modulo="abertura"><AberturaPage /></ProtectedRoute>} />
          <Route path="/cadastro-em-massa" element={<ProtectedRoute auth={auth} modulo="cadastro_massa"><CadastroMassaPage auth={auth} /></ProtectedRoute>} />
          <Route path="/importar" element={<ProtectedRoute auth={auth} modulo="backup"><ImportarPage /></ProtectedRoute>} />
          <Route path="/backup" element={auth.user?.perfil === 'super_admin' ? <PlanGate module="backup"><SystemBackupPage /></PlanGate> : <NoPermission />} />
          {/* developer-only:start */}
          <Route path="/developer" element={auth.user?.perfil === 'super_admin' ? <DeveloperPage /> : <NoPermission />} />
          {/* developer-only:end */}
          <Route path="/whatsapp" element={<ProtectedRoute auth={auth} modulo="whatsapp"><WhatsAppPage /></ProtectedRoute>} />
          <Route path="/configuracoes" element={<ProtectedRoute auth={auth} modulo="configuracoes"><ConfiguracoesPage theme={theme} onThemeChange={setTheme} /></ProtectedRoute>} />
          <Route path="/usuarios-sistema" element={<ProtectedRoute auth={auth} modulo="usuarios"><UsuariosSistemaPage /></ProtectedRoute>} />
          <Route path="/sem-permissao" element={<NoPermission />} />
          <Route path="*" element={<Navigate to={getFirstAllowedRoute(auth)} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function ProtectedRoute({ auth, modulo, children }) {
  if (!hasPermission(auth, modulo, 'visualizar')) {
    return <Navigate to={getFirstAllowedRoute(auth)} replace />;
  }
  return <PlanGate module={modulo}>{children}</PlanGate>;
}

function NoPermission() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-center text-sm font-semibold text-slate-500">
      Você não possui permissão para realizar esta ação.
    </div>
  );
}

export default function App() {
  return <LicensePlanProvider><AppContent /></LicensePlanProvider>;
}
